import type { IBallModel } from '@clsplus/cls-plus-data-models'

import { RequestHandler } from '../data/api/RequestHandler'
import Auth from './auth'
import {
  canRestorePrematchOvers,
  hasPositiveOvers,
  isKnownMatchFormat,
  isLimitedOversFormat,
} from './matchSettingsValidation'
import { isSampleMatch } from './sampleMatch'

export type RecoverableMatchFormat = 1 | 2 | 3 | 4

export type RecoveryMatchMetadata = {
  format: RecoverableMatchFormat
  maxOvers: number | null
  canRestoreOvers: boolean
}

export const recoveryFormatName = (format: RecoverableMatchFormat) =>
  format === 1 ? 'Multi-day' : format === 2 ? 'One Day' : format === 3 ? 'T20' : 'T10'

export const recoveryDeliveryInProgress = (ball: IBallModel): boolean => {
  if (ball.confirmed) return false
  // Scoring creates a blank next ball before Bowler Running In. That placeholder
  // can remain untouched during recovery; a delivery with input must be completed first.
  return !!(
    ball.timestamps.bowlerRunningIn ||
    ball.timestamps.firstInput ||
    ball.timestamps.runsConfirmed ||
    ball.timestamps.confirmed ||
    ball.timestamps.lastUpdated ||
    ball.runsBat !== null ||
    ball.runsExtra !== null ||
    ball.extrasTypeId !== null ||
    ball.pendingDismissal ||
    ball.pendingReview ||
    ball.dismissal ||
    ball.review ||
    ball.inningsProgress ||
    ball.incomplete ||
    ball.textDescription ||
    ball.allRun ||
    ball.shortRun ||
    ball.appeal
  )
}

export type MatchSettingsRecoveryState = {
  matchId: string
  matchFormatId: number | null
  maxOvers: number | null
  canRestoreOvers: boolean
  mode: string
  socketMatchId?: string
  connected: boolean
  primary: boolean
  assignedRole: boolean
  manualScoring: boolean
  timeMachine: boolean
  ballsLoaded: boolean
  deliveryInProgress: boolean
  available: boolean
}

export const matchSettingsRecoveryBlock = (state: MatchSettingsRecoveryState): string | null => {
  if (!state.available) return 'Open the match settings again to retry.'
  if (isSampleMatch(state.matchId) || state.mode !== 'advanced' || state.manualScoring || state.timeMachine) {
    return 'Recovery is available only during live advanced scoring.'
  }
  if (state.matchFormatId !== 0 && state.matchFormatId !== null) {
    if (!isLimitedOversFormat(state.matchFormatId) || hasPositiveOvers(state.maxOvers)) {
      return 'The match already has valid format and overs settings.'
    }
    if (!state.canRestoreOvers) return 'Overs can only be restored before the match starts and before innings exist.'
  }
  if (!state.connected || !state.primary || !state.assignedRole || state.socketMatchId !== state.matchId) {
    return 'The connected primary scout must restore the match format.'
  }
  if (!state.ballsLoaded) return 'Wait for delivery data to load before restoring the format.'
  if (state.deliveryInProgress) return 'Finish or cancel the current delivery before restoring the format.'
  return null
}

export const fetchRecoveryMatchFormat = async (matchId: string): Promise<RecoveryMatchMetadata> => {
  const response = await RequestHandler({
    method: 'GET',
    url: `${import.meta.env.VITE_API_URL}match/${encodeURIComponent(matchId)}`,
    params: { bypassLookup: 'true', mode: 'postMatch' },
    headers: { Authorization: `Bearer ${Auth.getTokens()?.accessToken}` },
  })
  if (response instanceof Error || !response.ok) throw new Error('Unable to check the match format. Please retry.')
  const data = await response.json()
  if (
    data?.id !== matchId ||
    (data.matchFormatId !== 1 && data.matchFormatId !== 2 && data.matchFormatId !== 3 && data.matchFormatId !== 4)
  ) {
    throw new Error('A supported match format is not available yet. Please contact support.')
  }
  // Do not instantiate or apply the returned snapshot: only this metadata is needed.
  return {
    format: data.matchFormatId,
    maxOvers: typeof data.matchConfigs?.maxOvers === 'number' ? data.matchConfigs.maxOvers : null,
    canRestoreOvers:
      canRestorePrematchOvers(data.matchStatusId, 0) &&
      Array.isArray(data.matchTeams) &&
      data.matchTeams.every(
        (team: { innings?: unknown[] }) => Array.isArray(team.innings) && team.innings.length === 0
      ),
  }
}

export type MatchSettingsRecoveryContext = {
  state: () => MatchSettingsRecoveryState
  revision: () => readonly unknown[]
  hasPendingUpdates: () => Promise<boolean>
  readFormat: (matchId: string) => Promise<RecoveryMatchMetadata>
  apply: (format: RecoverableMatchFormat, automaticFreeHits: boolean, maxOvers?: number) => void
}

export type MatchSettingsRecoveryPreview = {
  matchId: string
  format: RecoverableMatchFormat
  maxOvers?: number
  revision: readonly unknown[]
}

const assertReady = (context: MatchSettingsRecoveryContext, revision: readonly unknown[]) => {
  const blocked = matchSettingsRecoveryBlock(context.state())
  if (blocked) throw new Error(blocked)
  const current = context.revision()
  if (current.length !== revision.length || current.some((value, index) => value !== revision[index])) {
    throw new Error('Scoring changed while checking the format. Check it again during a pause.')
  }
}

export const prepareMatchSettingsRecovery = async (
  context: MatchSettingsRecoveryContext
): Promise<MatchSettingsRecoveryPreview> => {
  const revision = context.revision()
  assertReady(context, revision)
  const matchId = context.state().matchId
  if (await context.hasPendingUpdates()) throw new Error('Wait for all match updates to sync, then retry.')
  assertReady(context, revision)
  const metadata = await context.readFormat(matchId)
  if (await context.hasPendingUpdates()) throw new Error('Wait for all match updates to sync, then retry.')
  assertReady(context, revision)
  const state = context.state()
  if (isKnownMatchFormat(state.matchFormatId) && state.matchFormatId !== metadata.format) {
    throw new Error('The saved format differs from this match. Contact support before restoring settings.')
  }
  let maxOvers: number | undefined
  if (isLimitedOversFormat(metadata.format) && !hasPositiveOvers(state.maxOvers) && state.canRestoreOvers) {
    if (!metadata.canRestoreOvers || !hasPositiveOvers(metadata.maxOvers)) {
      throw new Error('A verified pre-match overs limit is not available yet. Please contact support.')
    }
    maxOvers = metadata.maxOvers
  }
  return { matchId, format: metadata.format, maxOvers, revision }
}

export const applyMatchSettingsRecovery = async (
  context: MatchSettingsRecoveryContext,
  preview: MatchSettingsRecoveryPreview,
  automaticFreeHits: boolean
) => {
  assertReady(context, preview.revision)
  if (context.state().matchId !== preview.matchId)
    throw new Error('The selected match changed. Check its format again.')
  // Re-read on confirmation so a preview never silently applies an outdated format.
  const latest = await prepareMatchSettingsRecovery(context)
  if (latest.format !== preview.format || latest.maxOvers !== preview.maxOvers) {
    throw new Error('The match format or overs changed. Check them again before restoring.')
  }
  assertReady(context, preview.revision)
  context.apply(latest.format, automaticFreeHits, latest.maxOvers)
}
